import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { getCurrencySymbol, formatCompactNumber } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

export const ProductChannelMatrixChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, channels, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const { products, channelList, matrix, maxValue } = useMemo(() => {
        if (!finance?.breakdown) {
            return { products: [], channelList: [], matrix: {} as Record<string, Record<string, number>>, maxValue: 0 };
        }

        const productRevenue: Record<string, { name: string; total: number }> = {};
        const channelSet = new Set<string>();
        const matrixData: Record<string, Record<string, number>> = {};

        for (const item of finance.breakdown) {
            if (item.type === 'REVENUE' && item.category === 'product_sale') {
                const productId = item.entityId as string;
                const channelId = item.channelId as string;
                const name = (item.entityName as string) || productId;
                const value = Number(item.value) || 0;

                if (!productRevenue[productId]) {
                    productRevenue[productId] = { name, total: 0 };
                }
                productRevenue[productId].total += value;

                if (channelId) {
                    channelSet.add(channelId);
                    if (!matrixData[productId]) {
                        matrixData[productId] = {};
                    }
                    matrixData[productId][channelId] = (matrixData[productId][channelId] || 0) + value;
                }
            }
        }

        const topProducts = Object.entries(productRevenue)
            .sort(([, a], [, b]) => b.total - a.total)
            .slice(0, 10)
            .map(([id, info]) => ({ id, name: info.name }));

        const channelNames: { id: string; name: string }[] = [];
        for (const chId of channelSet) {
            const ch = channels?.find((c: any) => c.id === chId);
            channelNames.push({ id: chId, name: (ch?.channelType as string) || chId });
        }

        let max = 0;
        for (const product of topProducts) {
            for (const channel of channelNames) {
                const val = matrixData[product.id]?.[channel.id] || 0;
                if (val > max) max = val;
            }
        }

        return {
            products: topProducts,
            channelList: channelNames,
            matrix: matrixData,
            maxValue: max,
        };
    }, [finance?.breakdown, channels]);

    const isEmpty = useMemo(() => products.length === 0 || channelList.length === 0, [products, channelList]);

    useSetChartMetadata({ isEmpty });

    const getIntensity = (value: number): string => {
        if (maxValue === 0 || value === 0) return 'transparent';
        const ratio = value / maxValue;
        const alpha = Math.max(0.1, Math.min(0.9, ratio));
        return `rgba(99, 102, 241, ${alpha})`;
    };

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2 overflow-auto">
                <table className="w-full text-[10px] border-collapse">
                    <thead>
                        <tr>
                            <th className="text-left p-1.5 text-text-primary font-medium border-b border-border-subtle">
                                {t('charts.product', 'Product')}
                            </th>
                            {channelList.map((ch) => (
                                <th
                                    key={ch.id}
                                    className="text-center p-1.5 text-text-primary font-medium border-b border-border-subtle"
                                >
                                    {ch.name}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {products.map((product) => (
                            <tr key={product.id}>
                                <td className="p-1.5 text-text-primary border-b border-border-subtle truncate max-w-[120px]">
                                    {product.name}
                                </td>
                                {channelList.map((ch) => {
                                    const value = matrix[product.id]?.[ch.id] || 0;
                                    return (
                                        <td
                                            key={ch.id}
                                            className="text-center p-1.5 border-b border-border-subtle text-text-primary"
                                            style={{ backgroundColor: getIntensity(value) }}
                                        >
                                            {value > 0
                                                ? formatCompactNumber(value, currencySymbol)
                                                : '-'}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
